'use client';
import { env } from '@ctct/rise-env';
import { genericLogger } from '@ctct/rise-logging';
import { type ReactNode, useEffect } from 'react';

const logger = genericLogger.child({ component: 'branch' });

// Branch logs can be noisy, so we log most messages at info level
const log = (message: string, err: string | null) => {
  if (typeof err === 'string') {
    // Many errors are due to adblockers, ignore these
    if (err?.includes('adblock')) return;

    if (err?.includes('Failed to load chunk')) {
      logger.error(message + ` Reason: ${err}`);
      return;
    }
    if (err?.includes('Storage')) {
      logger.warn(message + ` Reason: ${err}`);
      return;
    }
  }
  logger.info(message + ` Reason: ${err ?? 'Unknown error'}`);
};

const BranchProvider = ({ userId, children }: { userId?: string; children: ReactNode }) => {
  useEffect(() => {
    const loadBranch = async () => {
      const branch = (await import('branch-sdk')).default;
      const branchKey = env('NEXT_PUBLIC_BRANCH_KEY');

      if (!branch) {
        logger.info(`Failed to load Branch SDK. Reason: Unable to load Branch SDK`);
        return;
      }

      if (!branchKey) {
        logger.info(`Failed to load Branch SDK. Reason: Branch key not found`);
        return;
      }

      branch.init(branchKey, {}, function (err, data) {
        if (err) log('Failed to initialize Branch SDK.', err);
      });

      if (userId) {
        branch.setIdentity(userId, (err, data) => {
          if (err) log('Failed to set Branch identity.', err);
        });
      } else {
        branch.logout((err) => {
          if (err) log('Failed to logout Branch identity.', err);
        });
      }
    };

    loadBranch().catch((e) => {
      log('Failed to load Branch SDK.', e.message);
    });
  }, [userId]);

  return <>{children}</>;
};

export { BranchProvider };
